import React, { useState } from 'react';

/**
 * EcoGem — the interactive Stig-ecosystem diamond from stiggroup.com.
 *
 * Four quadrants (top, right, bottom, left) each represent one of the
 * group companies.  Hovering a quadrant swaps the base gold layer for
 * the corresponding coloured segment; clicking navigates to the
 * external stiggroup.com sub-site.
 *
 * All layer images live in /public/brand/eco-gem/.  The container needs
 * a dark background because the layers use `mix-blend-mode: screen`.
 */
const BASE = '/brand/eco-gem';

// Mapping quadrant id → logo file shown below the animated diamond.
const LOGOS = {
  top:    '/logos/stig-group.png',
  right:  '/logos/stig-international.png',
  bottom: '/logos/ethical-mines.png',
  left:   '/logos/stig-rock.png',
};
const IDLE_LOGO = '/logos/stig-group.png';

const QUADRANTS = [
  {
    id: 'top',
    label: 'STIG Group',
    href: 'https://stiggroup.com/the-group.php',
    base: `${BASE}/diamond-base-layer-alpha.png`,
    active: `${BASE}/diamond-stig-group-background-alpha.png`,
  },
  {
    id: 'right',
    label: 'STIG International',
    href: 'https://stiggroup.com/stig-international.php',
    base: `${BASE}/diamond-stig-international-background-alpha.png`,
    active: `${BASE}/diamond-stig-international-layer-alpha.png`,
  },
  {
    id: 'bottom',
    label: 'Ethical Mines',
    href: 'https://stiggroup.com/ethic-mine.php',
    base: `${BASE}/diamond-ethical-mines-background-alpha.png`,
    active: `${BASE}/diamond-ethical-mines-layer-alpha.png`,
  },
  {
    id: 'left',
    label: 'STIG ROCK',
    href: 'https://stiggroup.com/stig-rock/index.php',
    base: `${BASE}/diamond-stig-rock-background-alpha.png`,
    active: `${BASE}/diamond-stig-rock-layer-alpha.png`,
  },
];

export default function EcoGem({ onHoverChange, current = null }) {
  const [hover, setHover] = useState(null);
  // The visual "active" facet is whatever the user is currently hovering,
  // OR — when nothing is hovered — the last facet the parent committed
  // (so the segment stays lit after each click-advance).
  const active = QUADRANTS.find((q) => q.id === (hover || current));

  const setHoverAndReport = (id) => {
    setHover(id);
    if (typeof onHoverChange === 'function') onHoverChange(id);
  };

  return (
    <div className="ecogem-wrap" data-testid="eco-gem">
      {/* Base gold diamond — always visible unless a quadrant is hovered */}
      <img
        className={`ecogem-ly ecogem-ly--base ${active ? 'is-dim' : ''}`}
        src={`${BASE}/diamond-base-layer-alpha.png`}
        alt=""
        draggable={false}
      />

      {/* Crown facet — the base layer doesn't include the top triangle, so we
          overlay the STIG-Group active layer permanently to complete the gold
          diamond at idle.  Dims when any quadrant is hovered. */}
      <img
        className={`ecogem-ly ecogem-ly--crown ${active ? 'is-dim' : ''} ${(hover || current) === 'top' ? 'is-off' : ''}`}
        src={`${BASE}/diamond-stig-group-background-alpha.png`}
        alt=""
        draggable={false}
      />

      {/* One active layer per quadrant — fades in on hover of that quadrant */}
      {QUADRANTS.map((q) => (
        <React.Fragment key={q.id}>
          <img
            className={`ecogem-ly ecogem-ly--bg ecogem-ly--bg-${q.id} ${(hover || current) === q.id ? 'is-on' : ''}`}
            src={q.base}
            alt=""
            draggable={false}
          />
          <img
            className={`ecogem-ly ecogem-ly--color ecogem-ly--color-${q.id} ${(hover || current) === q.id ? 'is-on' : ''}`}
            src={q.active}
            alt=""
            draggable={false}
          />
        </React.Fragment>
      ))}

      {/* Four triangular hit-zones — hover only. Clicks bubble up to the
          parent SectionEcosystem so a single click-anywhere in the section
          drives progression (no per-facet click handlers here to avoid
          double-firing with the section-level handler). */}
      {QUADRANTS.map((q) => (
        <button
          key={q.id}
          type="button"
          className={`ecogem-hit ecogem-hit--${q.id}`}
          aria-label={q.label}
          data-testid={`eco-gem-${q.id}`}
          onMouseEnter={() => setHoverAndReport(q.id)}
          onFocus={() => setHoverAndReport(q.id)}
          onMouseLeave={() => setHoverAndReport(null)}
          onBlur={() => setHoverAndReport(null)}
        />
      ))}
    </div>
  );
}
